import React from "react";
import { ClassNames, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { UploadedDocumentRowStatus } from "./UploadCustomerDocumentsForm.utils";

type Props = {
  name: string;
  tooltip?: string;
  status: UploadedDocumentRowStatus;
  allowDelete?: boolean;
  onDelete?: () => void;
};

export default function UploadedDocumentRow({
  name,
  tooltip,
  status,
  allowDelete,
  onDelete,
}: Props) {
  const { colors } = useTheme();

  const icon = status === "error" ? "alertCircle" : "fileCheck02";
  const iconColor = status === "error" ? colors.sysDestructiveDefault : colors.sysTextDefault;
  const nameColor = status === "error" ? colors.sysDestructiveDefault : undefined;

  const CheckIcon = ({ className }: { className?: string }) => (
    <UIcon icon="checkOutlined" color={colors.sysSuccessDefault} className={className} size={16} />
  );

  const renderRight = () => {
    if (status === "success") {
      return (
        <ClassNames>{({ css }) => <CheckIcon className={css({ marginRight: 8 })} />}</ClassNames>
      );
    }

    if (status === "error") {
      return (
        <Text description color={colors.sysDestructiveDefault}>
          Error
        </Text>
      );
    }

    if (status === "uploading") {
      return (
        <Text description color={colors.sysTextSecondary}>
          Uploading...
        </Text>
      );
    }

    if (status === "processing") {
      return (
        <Text description color={colors.sysTextSecondary}>
          Processing...
        </Text>
      );
    }

    if (!allowDelete) return null;

    return (
      <Tooltip content="Delete">
        <Button
          buttonType="tertiary"
          icon="trash01"
          iconProps={{
            color: colors.sysTextDefault,
          }}
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            onDelete?.();
          }}
        />
      </Tooltip>
    );
  };

  return (
    <FileContent>
      <Tooltip content={tooltip}>
        <Flex alignItems="center">
          <ClassNames>
            {({ css }) => (
              <>
                <UIcon
                  icon={icon}
                  color={iconColor}
                  className={css({ marginRight: 4 })}
                  size={16}
                />
                <EllipsisTooltipText
                  fullText={name}
                  containerClassname={css({
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    lineClamp: 1,
                    color: nameColor,
                  })}
                />
              </>
            )}
          </ClassNames>
        </Flex>
      </Tooltip>
      {renderRight()}
    </FileContent>
  );
}

const FileContent = styled(Flex)({
  gap: 24,
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
});
